/**
 * configService.ts
 * ----------------
 * Single source of truth for all runtime configuration.
 *
 * Resolution order (3-layer deep merge):
 *   default/<service>.json          ← base values
 *     ↓ merged with
 *   <qa|prod>/<service>.json        ← environment overrides
 *     ↓ merged with
 *   brands/<brand>/<service>.json   ← brand overrides (when a non-default brand is active)
 *
 * All JSON files are statically imported so webpack can bundle them for the client.
 * The resolved result is cached (per service + brand) for the module lifetime.
 *
 * What lives here (in JSON):
 *   Shopify storefront credentials, Amplify pool IDs, GTM IDs, Google client ID, Apple Sign-In config
 *
 * What stays in .env (server secrets only):
 *   SHOPIFY_ADMIN_STORE_FRONT_ACCESS_TOKEN — Shopify Admin API token (addtocart API route)
 *   SHOPIFY_ADMIN_STORE_DOMAIN             — Admin store domain
 */

// ─── Default configs ─────────────────────────────────────────────────────────
import defaultShopify   from './default/shopify.json';
import defaultAmplify   from './default/amplify.json';
import defaultAnalytics from './default/analytics.json';
import defaultGoogle    from './default/google.json';
import defaultApple     from './default/apple.json';

// ─── QA overrides ────────────────────────────────────────────────────────────
import qaAnalytics from './qa/analytics.json';

// ─── Brand overrides (single registry — edit brands/index.ts to add/remove) ─
import { BRAND_OVERRIDES, BRAND_KEYS, isBrandKey } from './brands';
export { BRAND_KEYS, isBrandKey };

// ─── Types ───────────────────────────────────────────────────────────────────

export type AppEnv   = 'qa' | 'prod';
export type BrandKey = string;

export interface ShopifyConfig {
  domain: string;
  storefrontAccessToken: string;
  adminDomain: string;
}

export interface AmplifyUserPoolConfig {
  userPoolId: string;
  userPoolClientId: string;
  identityPoolId: string;
  oauthDomain?: string;
}

export interface AmplifyConfig {
  customer: AmplifyUserPoolConfig;
  designer: AmplifyUserPoolConfig;
  supplier: AmplifyUserPoolConfig;
}

export interface AnalyticsConfig {
  gtmId: string;
  gtmIdInternal: string;
}

export interface GoogleConfig {
  clientId: string;
}

export interface AppleConfig {
  client_id: string;
  team_id: string;
  key_id: string;
  redirect_uri: string;
  scope: string;
  responseMode: string;
}

type ServiceConfigs = {
  shopify:   ShopifyConfig;
  amplify:   AmplifyConfig;
  analytics: AnalyticsConfig;
  google:    GoogleConfig;
  apple:     AppleConfig;
};

// ─── Layer maps ──────────────────────────────────────────────────────────────

const ENV_OVERRIDES: Record<AppEnv, Partial<Record<keyof ServiceConfigs, unknown>>> = {
  qa:   { analytics: qaAnalytics },
  prod: {},
};

const DEFAULTS: Record<keyof ServiceConfigs, unknown> = {
  shopify:   defaultShopify,
  amplify:   defaultAmplify,
  analytics: defaultAnalytics,
  google:    defaultGoogle,
  apple:     defaultApple,
};

// ─── Deep merge ──────────────────────────────────────────────────────────────
// Empty strings are skipped so placeholder brand entries ("domain": "") don't
// override a real default value until the brand is properly configured.
// Explicit null removes the key (use to prevent a default from leaking in).

function deepMerge<T extends Record<string, unknown>>(
  ...sources: Array<Partial<T> | Record<string, unknown>>
): T {
  const result: Record<string, unknown> = {};
  for (const source of sources) {
    if (!source) continue;
    for (const key of Object.keys(source)) {
      const val = source[key];
      if (val === undefined || val === '') continue;
      // Explicit null → remove the key (prevents default from leaking in)
      if (val === null) {
        delete result[key];
        continue;
      }
      if (typeof val === 'object' && !Array.isArray(val)) {
        result[key] = deepMerge(
          (result[key] as Record<string, unknown>) ?? {},
          val as Record<string, unknown>,
        );
      } else {
        result[key] = val;
      }
    }
  }
  return result as T;
}

// ─── Cache + env detection ───────────────────────────────────────────────────

const cache = new Map<string, unknown>();

/**
 * Detected app environment. `process.env.NEXT_PUBLIC_APP_ENV` is inlined as a
 * static string at build time by Next.js, so this evaluates correctly on both
 * server and client without needing the next.config.js `env:{}` block.
 */
const CURRENT_ENV: AppEnv =
  process.env.NEXT_PUBLIC_APP_ENV === 'qa' ? 'qa' : 'prod';

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Returns the fully merged config for `service`.
 * Layers: default → env-specific → brand-specific.
 *
 * @param service  'shopify' | 'amplify' | 'analytics' | 'google' | 'apple'
 * @param brand    Brand slug (default: 'nestingale')
 * @param env      Override detected env (rarely needed)
 */
export function resolveConfig<K extends keyof ServiceConfigs>(
  service: K,
  brand: BrandKey = 'nestingale',
  env: AppEnv = CURRENT_ENV,
): ServiceConfigs[K] {
  const cacheKey = `${service}:${brand}:${env}`;
  if (cache.has(cacheKey)) return cache.get(cacheKey) as ServiceConfigs[K];

  const base       = DEFAULTS[service]                          as Record<string, unknown>;
  const envLayer   = (ENV_OVERRIDES[env]?.[service]   ?? {})   as Record<string, unknown>;
  const brandLayer = (BRAND_OVERRIDES[brand]?.[service] ?? {}) as Record<string, unknown>;

  const resolved = deepMerge<Record<string, unknown>>(base, envLayer, brandLayer);
  cache.set(cacheKey, resolved);
  return resolved as unknown as ServiceConfigs[K];
}

/**
 * Convenience wrapper: resolves config for the default (nestingale) brand.
 * Use this everywhere the brand is not dynamic.
 */
export function resolveDefaultConfig<K extends keyof ServiceConfigs>(
  service: K,
): ServiceConfigs[K] {
  return resolveConfig(service, 'nestingale');
}
